<script setup>
// Modal wpisu w planerze — dodawanie i edycja. Kafle wybierają typ (przy edycji
// typ jest ustalony), pola pochodzą z komponentów per typ, a kształt żądania
// z useV2Entry. Pokazujemy wyłącznie pola, które API przyjmuje.
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useApi } from '~/composables/useApi.ts';
import { useV2Entry, ENTRY_TYPES } from '@/composables/useV2Entry.js';
import MealFields from '@/components/v2/add/meal-fields.vue';
import WorkoutFields from '@/components/v2/add/workout-fields.vue';
import SleepFields from '@/components/v2/add/sleep-fields.vue';
import TaskFields from '@/components/v2/add/task-fields.vue';
import NoteFields from '@/components/v2/add/note-fields.vue';
import ReportFields from '@/components/v2/add/report-fields.vue';
import EventFields from '@/components/v2/add/event-fields.vue';
import ConfirmDialog from '@/components/v2/confirm-dialog.vue';
import { useSemiProducts, withSemiProducts } from '@/composables/useSemiProducts.js';
import { useI18n } from 'vue-i18n';

const props = defineProps({
    show: { type: Boolean, default: false },
    day: { type: Date, required: true },
    initialType: { type: String, default: 'note' },
    // Wpis do edycji — jego obecność przestawia modal w tryb zmiany.
    entry: { type: Object, default: null },
    checklists: { type: Array, default: () => [] },
    workoutTypes: { type: Array, default: () => [] },
    meals: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    recentWorkouts: { type: Array, default: () => [] },
    // Endpoint zadań istnieje tylko tam, gdzie API go wdrożono.
    tasksAvailable: { type: Boolean, default: false },
    // Pola szkicu wypełnione z góry przy dodawaniu (np. z dyktowania: trening, posiłek).
    prefill: { type: Object, default: null },
    // Bez kafli typów — dialog otwarty z modułu, który dodaje tylko jeden typ (np. Forma).
    lockType: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'saved', 'deleted']);

const { t } = useI18n();
const api = useApi();
const {
    createDraft, draftFromEntry, applyMeal, applyWorkout, withIngredients, ingredientFromProduct,
    validate, buildRequest,
} = useV2Entry();

const types = computed(() => ENTRY_TYPES.filter(et => et.key !== 'task' || props.tasksAvailable));
const isEdit = computed(() => !!props.entry);

const type = ref(props.initialType);
const draft = ref(createDraft(props.day));
const errors = ref({});
const generalError = ref(null);
const saving = ref(false);
const confirmingDelete = ref(false);
const sheet = ref(null);

const current = computed(() => ENTRY_TYPES.find(et => et.key === type.value) ?? ENTRY_TYPES[0]);
const heading = computed(() => (isEdit.value ? current.value.editTitle : current.value.title));

// Reset ustawia typ i szkic naraz — zmiana typu nie może wtedy nadpisać szkicu (prefill).
let skipTypeReset = false;

const reset = () => {
    skipTypeReset = type.value !== props.initialType;
    type.value = props.initialType;
    draft.value = props.entry
        ? draftFromEntry(props.initialType, props.entry)
        : { ...createDraft(props.day), ...(props.prefill ?? {}) };
    errors.value = {};
    generalError.value = null;
    confirmingDelete.value = false;
};

// Otwarcie modalu zawsze startuje od świeżego szkicu: pustego albo z wpisu.
// Półprodukty w składzie wpisu udają produkty liczone w porcjach — wybiera się
// je z tej samej listy. Katalog jest też potrzebny do gramów składnika-półproduktu,
// gdy wpis startuje od zapisanego posiłku.
const { semiProducts, loadSemiProducts } = useSemiProducts();
const catalogProducts = computed(() => withSemiProducts(props.products, semiProducts.value));

watch(() => props.show, async (open) => {
    if (!open) return;
    loadSemiProducts();
    reset();
    await nextTick();
    // Selecty v2 to przyciski z `role=combobox`, nie natywne `<select>` — bez nich
    // fokus przeskakiwał na kolejne pole (np. z wyboru posiłku na wybór produktu).
    sheet.value?.querySelector('[role=combobox], input:not([type=hidden]), textarea, select')?.focus();
});

// Zmiana typu (tylko przy dodawaniu) czyści błędy, ale zachowuje dzień i godzinę.
watch(type, () => {
    if (skipTypeReset) { skipTypeReset = false; return; }
    if (isEdit.value) return;
    const keepDay = draft.value.day;
    const keepTime = draft.value.time;
    draft.value = { ...createDraft(keepDay), day: keepDay, time: keepTime };
    errors.value = {};
    generalError.value = null;
});

// Wybór zapisanego posiłku przepisuje nazwę, skład i sumy makr.
const pickMeal = (uuid) => {
    const meal = props.meals.find(m => m.uuid === uuid);
    draft.value = applyMeal(draft.value, meal ?? null);
};

// Wpis można zacząć też od pojedynczego produktu — wtedy on jest składem, a nazwa
// bierze się z produktu, dopóki nic nie wpisano ręcznie. Kolejny produkt dokłada
// się do składu, więc da się złożyć posiłek z kilku pozycji bez zapisywania go w katalogu.
const pickProduct = ({ product, weight }) => {
    if (!product) return;
    const list = [...(draft.value.ingredients ?? []), ingredientFromProduct(product, weight)];
    const next = withIngredients(draft.value, list);
    draft.value = { ...next, name: draft.value.name?.trim() ? draft.value.name : product.name };
};

// Zmiana składu od razu przelicza makra wpisu.
const updateIngredients = (list) => {
    draft.value = withIngredients(draft.value, list);
};

// Powtórzenie wcześniejszego treningu przepisuje jego parametry.
const pickWorkout = (uuid) => {
    const workout = props.recentWorkouts.find(w => w.uuid === uuid);
    draft.value = applyWorkout(draft.value, workout ?? null);
};

const updateNaps = (list) => {
    draft.value = { ...draft.value, naps: list };
};

// Przejście na ręczne makra zostawia skład po stronie API bez zmian.
const setMode = (mode) => {
    draft.value = mode === 'list'
        ? withIngredients(draft.value, draft.value.ingredients ?? [])
        : { ...draft.value, ingredientsMode: 'manual' };
};

// Nazwy pól z API → pola szkicu, żeby błąd trafił pod właściwy input.
const API_TO_FIELD = {
    planned_at: 'time', performed_at: 'time', reported_at: 'time', due_at: 'time',
    type: 'workoutType', duration_seconds: 'duration', active_calories: 'activeCalories',
    distance_meters: 'distanceKm', wake_up_at: 'wakeUpAt', checklist_uuid: 'checklistUuid',
    planned_for: 'day', name: 'name', text: 'text', content: 'content', title: 'title',
    bedtime: 'bedtime', quality: 'quality', date: 'day',
    starts_at: 'time', ends_at: 'endTime', start_date: 'day', end_date: 'endDay', location: 'location',
    startsAt: 'time', endsAt: 'endTime', startDate: 'day', endDate: 'endDay',
};

const close = () => { if (!saving.value) emit('close'); };

const handleError = (error) => {
    const status = error.statusCode ?? error.status ?? error.response?.status;
    const apiErrors = error.data?.errors ?? [];
    if (status === 422 && apiErrors.length) {
        const mapped = {};
        const rest = [];
        for (const e of apiErrors) {
            const field = API_TO_FIELD[e.property];
            if (field) mapped[field] = e.message;
            else rest.push(e.message);
        }
        errors.value = mapped;
        if (rest.length) generalError.value = rest.join(' ');
        else if (!Object.keys(mapped).length) generalError.value = t('entry.dialog.checkData');
    } else if (status === 404 && type.value === 'task') {
        generalError.value = t('entry.dialog.tasksUnsupported');
    } else {
        generalError.value = t('entry.dialog.saveFailed');
    }
};

const save = async () => {
    if (saving.value) return;
    errors.value = {};
    generalError.value = null;

    const found = validate(type.value, draft.value);
    if (found.length) {
        errors.value = Object.fromEntries(found.map(e => [e.field, e.message]));
        return;
    }

    const { method, url, body } = buildRequest(type.value, draft.value, props.entry);
    saving.value = true;
    try {
        await api[method](url, body);
        emit('saved', type.value);
    } catch (error) {
        handleError(error);
    } finally {
        saving.value = false;
    }
};

const remove = () => {
    if (!isEdit.value || saving.value) return;
    confirmingDelete.value = true;
};

const confirmRemove = async () => {
    const { url } = buildRequest(type.value, draft.value, props.entry);
    saving.value = true;
    try {
        await api.delete(url);
        confirmingDelete.value = false;
        emit('deleted', type.value);
    } catch {
        generalError.value = t('entry.dialog.deleteFailed');
        confirmingDelete.value = false;
    } finally {
        saving.value = false;
    }
};

// Skróty łapiemy na dokumencie — Escape ma działać także po kliknięciu w tło,
// gdy focus wyszedł z pól modalu.
const onKeydown = (event) => {
    if (!props.show) return;
    // Gdy pyta modal potwierdzenia, klawisze należą do niego.
    if (confirmingDelete.value) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); save(); }
};

onMounted(() => document.addEventListener('keydown', onKeydown));
onUnmounted(() => document.removeEventListener('keydown', onKeydown));
</script>

<template>
    <div
        v-if="show"
        class="fixed inset-0 z-[60] flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
    >
        <div class="fixed inset-0 bg-[#211E1A]/25" @click="close"></div>

        <div
            ref="sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="v2-add-title"
            class="v2-sheet relative w-full max-w-[588px] bg-white rounded-2xl shadow-[0_24px_60px_-20px_rgba(33,30,26,0.45)] mt-6 mb-8"
        >
            <div class="px-6 pt-5 pb-4">
                <div class="flex items-start justify-between gap-4 mb-4">
                    <h2 id="v2-add-title" class="v2-display text-[20px] leading-none text-[#211E1A]">{{ heading }}</h2>
                    <button
                        type="button"
                        :title="t('entry.dialog.close')"
                        class="w-7 h-7 -mt-1 rounded-full flex items-center justify-center text-[#8A8378] hover:text-[#211E1A] hover:bg-[#F2ECE1] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                        @click="close"
                    >
                        <i class="pi pi-times text-xs"></i>
                    </button>
                </div>

                <!-- Wybór typu wpisu; przy edycji typ jest ustalony -->
                <div v-if="!isEdit && !lockType" class="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-5">
                    <button
                        v-for="option in types"
                        :key="option.key"
                        type="button"
                        class="py-3 rounded-xl flex flex-col items-center gap-1.5 border transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                        :class="type === option.key
                            ? 'border-[#C6BDAC] bg-[#F2ECE1] text-[#211E1A]'
                            : 'border-transparent bg-[#FAF7F2] text-[#8A8378] hover:text-[#211E1A]'"
                        :aria-pressed="type === option.key"
                        @click="type = option.key"
                    >
                        <i :class="option.icon" class="text-[15px]"></i>
                        <span class="text-[11.5px] font-medium">{{ option.label }}</span>
                    </button>
                </div>

                <MealFields
                    v-if="type === 'meal'"
                    :draft="draft"
                    :errors="errors"
                    :meals="meals"
                    :products="catalogProducts"
                    :is-edit="isEdit"
                    @pick-meal="pickMeal"
                    @pick-product="pickProduct"
                    @update-ingredients="updateIngredients"
                    @set-mode="setMode"
                />
                <WorkoutFields
                    v-else-if="type === 'workout'"
                    :draft="draft"
                    :errors="errors"
                    :workout-types="workoutTypes"
                    :recent-workouts="recentWorkouts"
                    :is-edit="isEdit"
                    @pick-workout="pickWorkout"
                />
                <SleepFields
                    v-else-if="type === 'sleep'"
                    :draft="draft"
                    :errors="errors"
                    @update-naps="updateNaps"
                />
                <TaskFields v-else-if="type === 'task'" :draft="draft" :errors="errors" :checklists="checklists" />
                <NoteFields v-else-if="type === 'note'" :draft="draft" :errors="errors" />
                <EventFields v-else-if="type === 'event'" :draft="draft" :errors="errors" :entry="entry" />
                <ReportFields v-else :draft="draft" :errors="errors" />

                <p v-if="generalError" class="text-[12.5px] text-[#B5643C] mt-4">{{ generalError }}</p>
            </div>

            <div class="px-6 py-4 border-t border-[#EFE9DF] flex items-center justify-between gap-4">
                <button
                    v-if="isEdit"
                    type="button"
                    class="text-[12.5px] text-[#A29A8E] hover:text-[#B5643C] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B] rounded"
                    :disabled="saving"
                    @click="remove"
                >
                    {{ t('entry.dialog.delete') }}
                </button>
                <span v-else></span>

                <span class="flex items-center gap-4">
                    <button
                        type="button"
                        class="text-[13px] text-[#6B655C] hover:text-[#211E1A] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B] rounded"
                        @click="close"
                    >
                        {{ t('entry.dialog.cancel') }}
                    </button>
                    <button
                        type="button"
                        class="h-9 px-4 rounded-full bg-[#211E1A] text-[#FBF9F4] text-[13px] font-semibold flex items-center gap-2 hover:bg-[#302B26] disabled:opacity-60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C0764B]"
                        :disabled="saving"
                        @click="save"
                    >
                        {{ saving ? t('entry.dialog.saving') : (isEdit ? t('entry.dialog.saveChanges') : t('entry.dialog.save')) }}
                        <span v-if="!saving" class="text-[11px] text-[#B8B0A3]">⌘↵</span>
                    </button>
                </span>
            </div>
        </div>

        <ConfirmDialog
            :show="confirmingDelete"
            :title="t('entry.dialog.deleteTitle', { type: current.label.toLowerCase() })"
            :busy="saving"
            :message="entry?.linked
                ? t('entry.dialog.deleteLinkedEventMessage')
                : t('entry.dialog.deleteMessage')"
            @close="confirmingDelete = false"
            @confirm="confirmRemove"
        />
    </div>
</template>

<style scoped>
.v2-sheet {
    animation: v2-sheet-in 0.18s ease-out;
}

@keyframes v2-sheet-in {
    from { opacity: 0; transform: translateY(-6px) scale(0.99); }
    to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
    .v2-sheet {
        animation: none;
    }
}
</style>
